import VirtualScroller from "components/general/VirtualScroller/VirtualScroller";
import { apisStore } from "context/GlobalStates";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import {
  JSXElementConstructor,
  Key,
  ReactElement,
  ReactFragment,
  ReactPortal,
  useEffect,
  useState,
} from "react";
import NotificationPopUp from "util/NotificationPopUp";
import Utils from "util/Utils";
import {
  ExecutionResultDTO,
  JobrunnerLogsApi,
  ResultStatus,
} from "../../../serviceapi/jobRunnerApi";

enum DropdownNames {
  TASK_ID = "taskId",
  STATUS = "status",
}

enum DateType {
  FROM = "fromDate",
  TO = "toDate",
}

interface LogsDialogProps {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  executionsData: ExecutionResultDTO[];
  refreshLogs: () => void;
  initialTaskId?: string | null;
}

const statusConfig: Record<string, { icon: string; color: string }> = {
  [ResultStatus.Success]: { icon: "pi-check", color: "#AAFF00" },
  [ResultStatus.Failure]: { icon: "pi-times", color: "red" },
  [ResultStatus.FailureInputFetching]: { icon: "pi-times", color: "red" },
  [ResultStatus.FailureOutputParsing]: { icon: "pi-times", color: "red" },
  [ResultStatus.FailureOutputWriting]: { icon: "pi-times", color: "red" },
};

const LogsDialog = ({
  isVisible,
  setIsVisible,
  executionsData,
  refreshLogs,
  initialTaskId,
}: LogsDialogProps) => {
  const [selectedTaskId, setSelectedTaskId] = useState<string>(
    executionsData.find((e) => e.taskId === initialTaskId)?.taskId ??
      executionsData[0]?.taskId
  );
  const [selectedStatus, setSelectedStatus] = useState<string>();
  const [selectedFromDate, setSelectedFromDate] = useState<Date | null>(null);
  const [selectedToDate, setSelectedToDate] = useState<Date | null>(null);

  const [selectedExecutionLogs, setSelectedExecutionLogs] = useState<
    File | undefined
  >(undefined);

  const [filteredExecutionsData, setFilteredExecutionsData] =
    useState<ExecutionResultDTO[]>(
      executionsData.filter(
        (e) =>
          e.taskId ===
          (executionsData.find((ex) => ex.taskId === initialTaskId)?.taskId ??
            executionsData[0]?.taskId)
      )
    );

  const [selectedExecution, setSelectedExecution] =
    useState<ExecutionResultDTO>();

  const apiJobRunnerLogs = apisStore.getState()?.jobRunner
    ?.jobrunnerLogsApi as JobrunnerLogsApi;

  const [logLines, setLogLines] = useState<JSX.Element[]>([]);
  const [lineHeights, setLineHeights] = useState<number[]>([]);

  const fetchLogsByExecutionsId = async (
    executionId: string
  ): Promise<File | undefined> => {
    try {
      const response = await apiJobRunnerLogs.getExecutionLog(executionId, {
        headers: { Accept: "text/plain" },
        responseType: "text",
      });

      if (response.data) {
        const logsArray = response.data
          .toString()
          .split("\n")
          .map(
            (
              line:
                | string
                | number
                | boolean
                | ReactElement<any, string | JSXElementConstructor<any>>
                | ReactFragment
                | ReactPortal
                | null
                | undefined,
              index: Key | null | undefined
            ) => (
              <div key={index} style={{ whiteSpace: "pre-wrap" }}>
                {line}
              </div>
            )
          );

        setLogLines(logsArray);
        setSelectedExecutionLogs(new File([response.data], "logs.log"));
        setLineHeights(logsArray.map(() => 20));
        return new File([response.data], "logs.log", { type: "text/plain" });
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
      NotificationPopUp.show("Failed to fetch logs.", "error");
    }
  };

  useEffect(() => {
    if (executionsData && executionsData.length > 0) {
      const initialTaskExecutions = executionsData.filter(
        (execution) => execution.taskId === initialTaskId
      );
      const targetTaskId =
        initialTaskExecutions.length > 0
          ? initialTaskExecutions[0].taskId
          : executionsData[0].taskId;

      setSelectedTaskId(targetTaskId);
      setFilteredExecutionsData(
        executionsData.filter((execution) => execution.taskId === targetTaskId)
      );
      setSelectedStatus(undefined);
      setSelectedFromDate(null);
      setSelectedToDate(null);

      if (initialTaskExecutions.length > 0) {
        const lastExecution = initialTaskExecutions.reduce(
          (latest, execution) =>
            new Date(execution.startTime).getTime() >
            new Date(latest.startTime).getTime()
              ? execution
              : latest
        );
        setSelectedExecution(lastExecution);
        fetchLogsByExecutionsId(lastExecution.executionId);
      }
    } else {
      setFilteredExecutionsData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionsData, initialTaskId]);

  const uniqueTaskIds = Array.from(
    new Set(executionsData?.map((execution) => execution.taskId))
  ).map((taskId) => ({ label: taskId, value: taskId }));

  const uniqueStatuses = Array.from(
    new Set(executionsData?.map((execution) => execution.status))
  ).map((status) => ({ label: status, value: status }));

  const handleFilterExecutionsData = (
    e?: DropdownChangeEvent | { fromDate?: Date | null; toDate?: Date | null },
    dropdownName?: DropdownNames
  ) => {
    let newTaskId = selectedTaskId;
    let newStatus = selectedStatus;
    let newFromDate = selectedFromDate;
    let newToDate = selectedToDate;

    if (e) {
      if ("value" in e && dropdownName) {
        if (dropdownName === DropdownNames.TASK_ID) {
          newTaskId = e.value as string;
          setSelectedTaskId(newTaskId);
        } else if (dropdownName === DropdownNames.STATUS) {
          newStatus = e.value ?? undefined;
          setSelectedStatus(newStatus);
        }
      }

      if ("fromDate" in e || "toDate" in e) {
        if (e.fromDate !== undefined) {
          newFromDate = e.fromDate ?? null;
          setSelectedFromDate(newFromDate);
        }
        if (e.toDate !== undefined) {
          newToDate = e.toDate ?? null;
          setSelectedToDate(newToDate);
        }
      }
    }

    let filtered = executionsData;

    if (newTaskId) {
      filtered = filtered.filter((execution) => execution.taskId === newTaskId);
    }

    if (newStatus) {
      filtered = filtered.filter((execution) => execution.status === newStatus);
    }

    if (newFromDate || newToDate) {
      filtered = filtered.filter((execution) => {
        const executionStartTime = new Date(execution.startTime).getTime();
        return (
          (!newFromDate || executionStartTime >= newFromDate.getTime()) &&
          (!newToDate || executionStartTime <= newToDate.getTime())
        );
      });
    }

    setFilteredExecutionsData(filtered);
  };

  const formatDuration = (duration: number): string => {
    const hours = Math.floor(duration / (1000 * 60 * 60));
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((duration % (1000 * 60)) / 1000);
    const milliseconds = duration % 1000;

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0) parts.push(`${seconds}s`);
    if (milliseconds > 0 && seconds === 0) parts.push(`${milliseconds}ms`);

    return parts.join(" ") || "0ms";
  };

  const durationTemplate = ({ startTime, endTime }: ExecutionResultDTO) => {
    if (!startTime || !endTime) return <div>Invalid Duration</div>;

    const duration =
      new Date(endTime).getTime() - new Date(startTime).getTime();
    return <div>{formatDuration(duration)}</div>;
  };

  const statusTemplate = (rowData: ExecutionResultDTO) => {
    const status = rowData.status as keyof typeof statusConfig;
    const config = statusConfig[status];

    return (
      <div>
        <i
          className={`pi ${config.icon}`}
          style={{ color: config.color }}
          data-pr-tooltip={status}
          data-pr-position="bottom"
        />
        <Tooltip target={`[data-pr-tooltip="${status}"]`} />
      </div>
    );
  };

  const formatedDateTemplateOnlyDate = (rowData: ExecutionResultDTO) => {
    return (
      <div>
        {Utils.formatISOToBasicDateSplittingTime(rowData.startTime, false)}
      </div>
    );
  };

  const formatedDateTemplateOnlyTime = (rowData: ExecutionResultDTO) => {
    return (
      <div>
        {Utils.formatISOToBasicDateSplittingTime(rowData.startTime, true)}
      </div>
    );
  };

  const datePickerTemplate = (
    selectedDate: Date | null,
    dateType: DateType
  ) => {
    return (
      <div className="flex flex-column gap-2">
        <label htmlFor="dateFrom">
          Date {dateType === DateType.FROM ? "From" : "To"}
        </label>
        <Calendar
          className="w-12rem"
          dateFormat="yy-mm-dd"
          placeholder={`${dateType === DateType.FROM ? "From" : "To"} Date`}
          value={selectedDate}
          onChange={(e) => {
            if (e.value instanceof Date || e.value === null) {
              const updatedFromDate =
                dateType === DateType.FROM ? e.value : selectedFromDate;
              const updatedToDate =
                dateType === DateType.TO ? e.value : selectedToDate;

              handleFilterExecutionsData({
                fromDate: updatedFromDate,
                toDate: updatedToDate,
              });

              if (dateType === DateType.FROM) {
                setSelectedFromDate(e.value);
              } else {
                setSelectedToDate(e.value);
              }
            }
          }}
          showTime
          hourFormat="24"
          showIcon
          showButtonBar
        />
      </div>
    );
  };

  const headerTemplate = () => {
    return (
      <div className="flex justify-content-start align-items-center gap-1">
        <div>{selectedTaskId}</div>
        <div>Logs</div>
      </div>
    );
  };

  const displayLogs = () => {
    if (logLines && selectedExecution) {
      if (logLines.length > 0) {
        return (
          <VirtualScroller
            lines={logLines}
            count={logLines.length}
            linesSize={lineHeights}
            autoScroll={false}
          />
        );
      }
      return (
        <div className="flex justify-content-center align-items-center h-full">
          Log is empty
        </div>
      );
    }
    return (
      <div className="flex justify-content-center align-items-center h-full">
        <div>Please select an execution</div>
      </div>
    );
  };

  return (
    <Dialog
      header={headerTemplate}
      visible={isVisible}
      style={{ width: "75%", position: "absolute" }}
      onHide={() => setIsVisible(false)}
    >
      <div className="flex flex-column gap-3">
        <div className="flex flex-row justify-content-between align-items-center">
          <div className="flex flex-row gap-3">
            <div className="flex flex-column gap-2">
              <label htmlFor="taskId">Task Id</label>
              <Dropdown
                className="w-12rem"
                placeholder="Select task id"
                value={selectedTaskId}
                options={uniqueTaskIds}
                onChange={(e) =>
                  handleFilterExecutionsData(e, DropdownNames.TASK_ID)
                }
              />
            </div>
            <div className="flex flex-column gap-2">
              <label htmlFor="status">Status</label>
              <Dropdown
                className="w-12rem"
                placeholder="Select status"
                value={selectedStatus}
                options={uniqueStatuses}
                onChange={(e) =>
                  handleFilterExecutionsData(e, DropdownNames.STATUS)
                }
                showClear
              />
            </div>
            {datePickerTemplate(selectedFromDate, DateType.FROM)}
            {datePickerTemplate(selectedToDate, DateType.TO)}
          </div>
          <div className="flex flex-row gap-3">
            <Button
              className="align-self-end"
              icon="pi pi-refresh"
              tooltip="Refresh"
              tooltipOptions={{ position: "bottom" }}
              onClick={refreshLogs}
            />
            <Button
              className="align-self-end"
              icon="pi pi-download"
              tooltip="Download"
              tooltipOptions={{ position: "bottom" }}
              onClick={() => {
                if (selectedExecutionLogs)
                  Utils.downloadFile(selectedExecutionLogs);
              }}
              disabled={!selectedExecutionLogs}
            />
          </div>
        </div>
        <div className="flex flex-row gap-3">
          <div className="w-4">
            {filteredExecutionsData || executionsData ? (
              <div style={{ overflowX: "hidden", width: "100%" }}>
                <DataTable
                  value={filteredExecutionsData}
                  scrollable
                  scrollHeight="600px"
                  virtualScrollerOptions={{ itemSize: 40 }}
                  selectionMode="single"
                  selection={selectedExecution}
                  onSelectionChange={(e) => {
                    if (e.value) {
                      const selectedExecution = filteredExecutionsData.find(
                        (execution) =>
                          execution.executionId === e.value.executionId
                      );
                      setSelectedExecution(selectedExecution);
                      fetchLogsByExecutionsId(e.value.executionId);
                    } else {
                      setSelectedExecution(undefined);
                    }
                  }}
                  dataKey="executionId"
                  size="small"
                >
                  <Column
                    field="startTime"
                    header="Start Time"
                    body={formatedDateTemplateOnlyDate}
                  />
                  <Column
                    field="startTime"
                    body={formatedDateTemplateOnlyTime}
                  />
                  <Column header="Duration" body={durationTemplate} />
                  <Column
                    field="status"
                    header="Status"
                    body={statusTemplate}
                  />
                </DataTable>
              </div>
            ) : (
              <div>No available data</div>
            )}
          </div>
          <div className="flex w-8 ">
            <div className="w-full">{displayLogs()}</div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default LogsDialog;
